import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { setStoredToken } from "@/lib/api";
import { isAdminRole } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

/**
 * AuthCallbackPage — receives the MatchBroker JWT from our Google OAuth
 * backend callback via URL fragment `#token=<jwt>`.
 *
 * The backend does 100% of the authorization-code exchange server-side
 * (Google → our /api/v1/auth/google/callback → this URL). By the time
 * the browser lands here, the user has already been found-or-created and
 * their JWT minted. We simply:
 *   1. Read the token from the fragment.
 *   2. Store it under the same key as email/password login.
 *   3. Refresh /auth/me → route the user based on their state.
 *
 * A useRef guard prevents double-processing under React 18 StrictMode.
 * The URL fragment is scrubbed after adoption so the token can't leak
 * via referer / session-restore.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const params = new URLSearchParams(
        hash.startsWith("#") ? hash.slice(1) : hash
      );
      const token = params.get("token");
      if (!token) {
        setError(
          "Google sign-in didn't complete. Please try signing in again."
        );
        return;
      }

      try {
        setStoredToken(token);
        const user = await refreshMe();
        // refreshMe swallows 401s (invalid/expired JWT) and returns null.
        if (!user) {
          setStoredToken(null);
          setError(
            "Your Google sign-in link has expired or is invalid. Please try again."
          );
          return;
        }
        // Scrub the token from the URL immediately.
        window.history.replaceState(null, "", window.location.pathname);
        const target =
          isAdminRole(user.role)
            ? "/control-panel-x9"
            : user.profile_incomplete
              ? "/complete-profile"
              : !user.is_verified
                ? "/verification-pending"
                : "/dashboard";
        navigate(target, { replace: true });
      } catch {
        setError(
          "We couldn't finish Google sign-in. Please try signing in again."
        );
      }
    })();
  }, [navigate, refreshMe]);

  return (
    <div className="mb-page flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md rounded-3xl border border-gray-100 bg-white p-10 text-center shadow-mb-card mb-rise">
        <div className="mb-6 flex items-center justify-center">
          <BrandLogo className="h-10" />
        </div>
        {!error ? (
          <div
            className="flex items-center justify-center gap-2 text-sm text-gray-500"
            data-testid="google-callback-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Finishing Google sign-in…
          </div>
        ) : (
          <div data-testid="google-callback-error" className="space-y-6">
            <span className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-red-500">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
              Google sign-in failed
            </h1>
            <p className="text-sm text-gray-500">{error}</p>
            <Button
              onClick={() => navigate("/login", { replace: true })}
              className="h-11 w-full rounded-xl bg-[#1B2B4B] text-sm font-semibold text-white hover:bg-[#14213A]"
              data-testid="google-callback-back-to-login"
            >
              Back to sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
